import { useState, useEffect } from "react";
import { authAxios } from "@/utils/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { 
  Lock, 
  Unlock, 
  ChevronDown, 
  ChevronRight, 
  Save, 
  RotateCcw,
  Shield,
  Eye,
  EyeOff,
  Info,
} from "lucide-react";
import { toast } from "sonner";

// Category display config
const CATEGORY_CONFIG = {
  BASIC_PROFILE: { name: "Basic Profile", icon: "👤" },
  PERSONAL_IDENTITY: { name: "Personal Identity", icon: "🪪" },
  PHYSICAL_APPEARANCE: { name: "Physical Appearance", icon: "✨" },
  DEMOGRAPHICS_ORIGINS: { name: "Demographics & Origins", icon: "🌍" },
  REL_CONFIG: { name: "Relationship Config", icon: "💑" },
  REL_HISTORY_DYNAMICS: { name: "Relationship History", icon: "📖" },
  LOVE_ATTACHMENT_INTIMACY: { name: "Love & Attachment", icon: "💕" },
  REL_VALUES_BOUNDARIES: { name: "Values & Boundaries", icon: "🛡️" },
  CORE_PERSONALITY: { name: "Core Personality", icon: "🧠" },
  EMOTIONAL_ORIENTATION: { name: "Emotional Orientation", icon: "💫" },
  COMMUNICATION_CONFLICT: { name: "Communication Style", icon: "💬" },
  ATTACHMENT_PACE_SAFETY: { name: "Attachment & Safety", icon: "🤝" },
  BOUNDARIES_SENSITIVITIES: { name: "Boundaries", icon: "⚠️" },
  ENERGY_RHYTHM: { name: "Energy & Rhythm", icon: "⚡" },
  ATTRACTION_DESIRE: { name: "Attraction & Desire", icon: "🔥" },
  WORK_CAREER: { name: "Work & Career", icon: "💼" },
  LIFESTYLE_INTERESTS: { name: "Lifestyle & Interests", icon: "🎯" },
  HABITS_SELFCARE: { name: "Habits & Self-Care", icon: "🧘" },
  TRAVEL_LIFE_HISTORY: { name: "Travel & History", icon: "✈️" },
  SKILLS_CAPACITIES: { name: "Skills & Capacities", icon: "🎨" },
  PREFERENCES_SENSITIVITIES: { name: "Preferences", icon: "❤️" },
  FAMILY_HOME_LIFE: { name: "Family & Home", icon: "🏠" },
  DREAMS_VISION: { name: "Dreams & Vision", icon: "🌟" },
  SOCIAL_DYNAMICS: { name: "Social Dynamics", icon: "👥" },
  VALUES_WORLDVIEW: { name: "Values & Worldview", icon: "🌐" },
  BIO_DIGITAL_PRESENCE: { name: "Bio & Digital", icon: "📱" },
};

// Privacy level color coding
const getPrivacyColor = (level) => {
  if (level === 0) return "text-green-400 bg-green-400/10";
  if (level <= 4) return "text-blue-400 bg-blue-400/10";
  if (level <= 9) return "text-amber-400 bg-amber-400/10";
  if (level <= 13) return "text-orange-400 bg-orange-400/10";
  return "text-red-400 bg-red-400/10"; // Level 14 (Never)
};

const getPrivacyIcon = (level) => {
  if (level === 0) return <Unlock className="w-3.5 h-3.5" />;
  if (level === 14) return <EyeOff className="w-3.5 h-3.5" />;
  return <Lock className="w-3.5 h-3.5" />;
};

export default function PersonaSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fieldSpecs, setFieldSpecs] = useState([]);
  const [fieldPrivacy, setFieldPrivacy] = useState({});
  const [privacyLevels, setPrivacyLevels] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [updatedBy, setUpdatedBy] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    fetchDefaultSettings();
  }, []);

  const fetchDefaultSettings = async () => {
    try {
      setLoading(true);
      const response = await authAxios.get("/privacy-settings/admin/defaults");
      
      setFieldSpecs(response.data.field_specs || []);
      setFieldPrivacy(response.data.field_privacy || {});
      setPrivacyLevels(response.data.privacy_levels || {});
      setUpdatedBy(response.data.updated_by);
      setUpdatedAt(response.data.updated_at);
      setHasChanges(false);
    } catch (error) {
      console.error("Error fetching default privacy settings:", error);
      toast.error("Failed to load default privacy settings");
    } finally {
      setLoading(false);
    }
  };

  const handlePrivacyChange = (fieldKey, level) => {
    setFieldPrivacy(prev => ({
      ...prev,
      [fieldKey]: parseInt(level),
    }));
    setHasChanges(true);
  };

  const handleSaveAll = async () => {
    try {
      setSaving(true);
      await authAxios.patch("/privacy-settings/admin/defaults/bulk", {
        field_privacy: fieldPrivacy,
      });
      toast.success("Default privacy settings saved");
      setHasChanges(false);
      fetchDefaultSettings(); // Refresh to get updated_at
    } catch (error) {
      console.error("Error saving default settings:", error);
      toast.error("Failed to save default privacy settings");
    } finally {
      setSaving(false);
    }
  };

  const handleResetAll = async () => {
    if (!confirm("Reset all default privacy levels to 0 (Public)? This cannot be undone.")) {
      return;
    }
    
    try {
      setSaving(true);
      await authAxios.post("/privacy-settings/admin/defaults/reset");
      toast.success("All defaults reset to public");
      fetchDefaultSettings();
    } catch (error) {
      console.error("Error resetting defaults:", error);
      toast.error("Failed to reset defaults");
    } finally {
      setSaving(false);
    }
  };

  // Group fields by category
  const fieldsByCategory = fieldSpecs.reduce((acc, spec) => {
    const category = spec.category_key || "OTHER";
    if (!acc[category]) acc[category] = [];
    acc[category].push(spec);
    return acc;
  }, {});

  const formatFieldName = (key) => {
    return key.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-4 border-divine-gold border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-divine-charcoal border-divine-gold/20">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-divine-gold flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Default Privacy Settings
              </CardTitle>
              <CardDescription className="text-gray-400 mt-1">
                Configure default disclosure levels for all persona fields. These defaults apply to new matches.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={handleResetAll}
                disabled={saving}
                className="border-divine-gold/30 text-divine-gold hover:bg-divine-gold/10"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset All
              </Button>
              <Button
                onClick={handleSaveAll}
                disabled={!hasChanges || saving}
                className="bg-divine-gold text-divine-black hover:bg-divine-gold/90"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
          
          {/* Info bar */}
          <div className="flex items-center gap-4 mt-4 text-sm text-gray-400">
            <span className="flex items-center gap-1">
              <Info className="w-4 h-4" />
              {fieldSpecs.length} fields across {Object.keys(fieldsByCategory).length} categories
            </span>
            {updatedBy && (
              <span>
                Last updated by <span className="text-divine-gold">{updatedBy}</span>
                {updatedAt && ` on ${new Date(updatedAt).toLocaleDateString()}`}
              </span>
            )}
            {hasChanges && (
              <Badge className="bg-amber-500/20 text-amber-400">Unsaved changes</Badge>
            )}
          </div>
        </CardHeader>
      </Card>

      {/* Privacy Level Legend */}
      <Card className="bg-divine-charcoal border-divine-gold/20">
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <span className="text-gray-400 font-medium">Privacy Levels:</span>
            <Badge className={`${getPrivacyColor(0)} flex items-center gap-1`}>
              <Unlock className="w-3 h-3" /> 0 - Always (Public)
            </Badge>
            <Badge className={`${getPrivacyColor(3)} flex items-center gap-1`}>
              <Lock className="w-3 h-3" /> 1-4 - Early Connection
            </Badge>
            <Badge className={`${getPrivacyColor(7)} flex items-center gap-1`}>
              <Lock className="w-3 h-3" /> 5-9 - Deeper Connection
            </Badge>
            <Badge className={`${getPrivacyColor(12)} flex items-center gap-1`}>
              <Lock className="w-3 h-3" /> 10-13 - Intimate
            </Badge>
            <Badge className={`${getPrivacyColor(14)} flex items-center gap-1`}>
              <EyeOff className="w-3 h-3" /> 14 - Never
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Categories */}
      <div className="space-y-2">
        {Object.entries(fieldsByCategory).map(([categoryKey, fields]) => (
          <CategorySection
            key={categoryKey}
            categoryKey={categoryKey}
            fields={fields}
            fieldPrivacy={fieldPrivacy}
            privacyLevels={privacyLevels}
            onPrivacyChange={handlePrivacyChange}
            formatFieldName={formatFieldName}
          />
        ))}
      </div>
    </div>
  );
}

// Category section component
function CategorySection({
  categoryKey,
  fields,
  fieldPrivacy,
  privacyLevels,
  onPrivacyChange,
  formatFieldName,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const config = CATEGORY_CONFIG[categoryKey] || { name: categoryKey, icon: "📁" };
  
  // Calculate category stats
  const levelCounts = fields.reduce((acc, f) => {
    const level = fieldPrivacy[f.field_key] || 0;
    if (level === 0) acc.public++;
    else if (level === 14) acc.never++;
    else acc.restricted++;
    return acc;
  }, { public: 0, restricted: 0, never: 0 });

  return (
    <Card className="bg-divine-charcoal border-divine-gold/20">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger className="w-full">
          <div className="flex items-center justify-between p-4 hover:bg-white/5 transition-colors">
            <div className="flex items-center gap-3">
              <span className="text-xl">{config.icon}</span>
              <span className="text-white font-medium">{config.name}</span>
              <Badge variant="secondary" className="bg-white/10 text-white/70">
                {fields.length} fields
              </Badge>
            </div>
            <div className="flex items-center gap-4">
              {/* Category stats */}
              <div className="flex items-center gap-2 text-xs">
                {levelCounts.public > 0 && (
                  <Badge className={getPrivacyColor(0)}>{levelCounts.public} public</Badge>
                )}
                {levelCounts.restricted > 0 && (
                  <Badge className={getPrivacyColor(7)}>{levelCounts.restricted} restricted</Badge>
                )}
                {levelCounts.never > 0 && (
                  <Badge className={getPrivacyColor(14)}>{levelCounts.never} never</Badge>
                )}
              </div>
              {isOpen ? (
                <ChevronDown className="w-4 h-4 text-white/50" />
              ) : (
                <ChevronRight className="w-4 h-4 text-white/50" />
              )}
            </div>
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="border-t border-white/10 p-4 space-y-2">
            {fields.map(spec => (
              <FieldPrivacyRow
                key={spec.field_key}
                spec={spec}
                privacyLevel={fieldPrivacy[spec.field_key] || 0}
                privacyLevels={privacyLevels}
                onPrivacyChange={onPrivacyChange}
                formatFieldName={formatFieldName}
              />
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

// Individual field row
function FieldPrivacyRow({
  spec,
  privacyLevel,
  privacyLevels,
  onPrivacyChange,
  formatFieldName,
}) {
  return (
    <div className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-white/5 transition-colors">
      <div className="flex items-center gap-3 flex-1">
        <div className={`p-1.5 rounded ${getPrivacyColor(privacyLevel)}`}>
          {getPrivacyIcon(privacyLevel)}
        </div>
        <div>
          <span className="text-white/90 text-sm font-medium">
            {formatFieldName(spec.field_key)}
          </span>
          <span className="text-white/40 text-xs ml-1.5">
            ({spec.field_key})
          </span>
          {spec.prompt_priority === "high" && (
            <Badge variant="outline" className="ml-2 text-[10px] border-amber-500/30 text-amber-400">
              priority
            </Badge>
          )}
        </div>
      </div>
      
      <Select
        value={String(privacyLevel)}
        onValueChange={(val) => onPrivacyChange(spec.field_key, val)}
      >
        <SelectTrigger className="w-[280px] bg-white/5 border-white/20 text-white">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-divine-charcoal border-divine-gold/20">
          {Object.entries(privacyLevels).map(([level, label]) => (
            <SelectItem 
              key={level} 
              value={level}
              className="text-white hover:bg-divine-gold/20"
            >
              <span className="flex items-center gap-2">
                <span className={`w-6 text-center ${getPrivacyColor(parseInt(level))}`}>
                  {level}
                </span>
                <span>{label}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
